<template>
  <div>
    <v-row class="mt-4 mx-4">
      <v-col cols="3">
        <v-card>
          <v-toolbar color="primary" dark>
            <v-toolbar-title
              >System Messages
              <span v-if="hasUnreadMessages" class="red-dot-indicator" title="Unread messages exist"></span
            ></v-toolbar-title>
          </v-toolbar>
          <v-list dense class="scrollable-list">
            <template v-if="messages.length > 0">
              <v-list-item
                v-for="(message, index) in messages"
                :key="index"
                @click="selectMessage(index)"
                :class="['message-item', { 'selected-message': selectedMessageIndex === index }]"
              >
                <v-list-item-content>
                  <v-list-item-title class="mb-2 subject-title">
                    {{ message.subject }}
                  </v-list-item-title>

                  <v-list-item-subtitle class="mb-2 content-text">
                    {{ formatContent(message.content) }}
                  </v-list-item-subtitle>

                  <v-list-item-subtitle class="text-caption text-muted">
                    {{ message.createdAt }}
                  </v-list-item-subtitle>
                </v-list-item-content>

                <v-list-item-action>
                  <v-chip v-if="!message.readAt" small class="new-chip ml-2"> New </v-chip>
                </v-list-item-action>
              </v-list-item>
            </template>
            <template v-else>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title class="subject-title"> No messages available </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-list>
        </v-card>
      </v-col>

      <v-col cols="9">
        <v-card>
          <v-card-title>
            {{ selectedMessage?.subject || 'Select a message' }}
          </v-card-title>
          <v-card-text>
            <div v-if="selectedMessage">
              <p><strong>Date:</strong> {{ selectedMessage.createdAt }}</p>
              <div class="email-wrapper">
                <div v-html="selectedMessage.content" class="formatted-content"></div>
              </div>
            </div>

            <div v-else>
              <p>No message selected</p>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { stateToTimezone } from '@/util/timezone';
import { utcToZonedTime, format } from 'date-fns-tz';

export default {
  name: 'SystemMessage',
  data() {
    return {
      messages: [],
      selectedMessageIndex: null,
    };
  },
  computed: {
    ...mapGetters('systemMessage', ['systemMessages']),
    ...mapGetters('app', ['clientConfig']),
    selectedMessage() {
      if (!this.messages) return null;
      return this.messages[this.selectedMessageIndex] || null;
    },
    hasUnreadMessages() {
      if (!this.messages || !Array.isArray(this.messages)) return false;
      return this.messages.some((message) => message && !message.readAt);
    },
  },
  methods: {
    ...mapActions('systemMessage', ['getSystemMessagesByClientId', 'markSystemMessageAsRead']),
    async selectMessage(index) {
      this.selectedMessageIndex = index;
      const message = this.messages[index];
      if (!message.readAt) {
        const today = new Date().toISOString();

        try {
          await this.markSystemMessageAsRead({
            messageId: message.id,
            readAt: today,
            clientId: this.clientConfig.id,
          });

          this.init();
        } catch (error) {
          console.error('Failed to mark message as read:', error);
        }
      }
    },
    formatContent(html) {
      // Create a temporary DOM element to parse HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;

      let output = '';

      tempDiv.childNodes.forEach((node) => {
        if (node.nodeName === 'OL' || node.nodeName === 'UL') {
          node.childNodes.forEach((li) => {
            if (li.nodeName === 'LI') {
              output += `${li.textContent.trim()}\n`;
            }
          });
        } else if (node.nodeName === 'P') {
          if (node.querySelector('img')) {
            output += `\n[Image]\n`;
          } else if (node.textContent.trim()) {
            output += `\n${node.textContent.trim()}\n`;
          }
        } else if (node.nodeName === 'IMG') {
          output += `[Image]\n`;
        } else if (node.nodeName === 'A') {
          output += `${node.textContent.trim()} (${node.getAttribute('href')})\n`;
        }
      });

      return output.trim();
    },
    async init() {
      await this.getSystemMessagesByClientId(this.clientConfig.id);
    },
  },
  async mounted() {
    if (!this.clientConfig.id) return;
    this.init();
  },
  watch: {
    systemMessages: {
      handler(newVal) {
        this.messages = newVal.map((message) => {
          const utcDate = new Date(message.createdAt);
          const zonedDate = utcToZonedTime(utcDate, stateToTimezone[this.clientConfig.state]);
          return {
            ...message,
            createdAt: format(zonedDate, 'yyyy-MM-dd h:mm a'),
          };
        });
      },
      deep: true,
    },
  },
};
</script>
<style lang="scss">
.red-dot-indicator {
  display: inline-block;
  width: 10px;
  height: 10px;
  background-color: #b50214;
  border-radius: 50%;
  margin-left: 10px;
}

.message-item {
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
  cursor: pointer;
}

.selected-message {
  background-color: #2e8dec;
  color: white !important;
}

.subject-title {
  font-size: 1rem !important;
  font-weight: bold;
}

.content-text {
  font-size: 0.8125rem !important;
  font-weight: normal;
  color: #424242;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
}

.text-wrap {
  white-space: normal;
  word-wrap: break-word;
}

.text-muted {
  color: #6c757d;
}

.scrollable-list {
  max-height: calc(100vh - 84px - 94px + 12px);
  overflow-y: auto;
}

.email-wrapper {
  padding: 20px;
  font-family: Arial, sans-serif;
  border: 1px solid #ccc;
  border-radius: 5px;
  background: #f9f9f9;
}

.formatted-content {
  line-height: 1;
}

.formatted-content a {
  color: #007bff;
  text-decoration: underline;
}

.formatted-content pre {
  background-color: #f4f4f4;
  padding: 10px;
  border-radius: 4px;
  overflow-x: auto;
}

.new-chip {
  background-color: #d83041 !important;
  color: white !important;
}
</style>
